import { makeObservable, flow, action, observable, computed, override } from "mobx";
import BaseDriveStore from "../Base/BaseDriveStore";
import { RenameModalStore } from "../RenameModal";

class SharedWithMeStore extends BaseDriveStore {
  // View mode
  viewMode = 'grid'; // 'grid' | 'table'

  // Move modal state
  isMoveModalOpen = false;
  itemIdBeingMoved = null;
  itemTypeBeingMoved = null; // 'folder' | 'document'

  // Share modal state
  isShareModalOpen = false;
  itemsToShare = [];
  currentSelectionType = null;
  selectedUsers = [];
  selectedPermission = '';
  pendingGrantees = [];
  isLoadingSharing = false;

  // Assign agent modal state
  isAssignAgentModalOpen = false;
  itemsToAssignAgent = [];
  selectedAgents = [];
  pendingAgents = [];
  currentAgents = [];
  isLoadingAgents = false;
  isLoadingAssign = false;

  // Duplicate modal state
  isDuplicateModalOpen = false;
  itemToDuplicate = null;

  // Filter state
  selectedOwner = null;

  constructor({ foldersStore, documentsStore, filesStore, agentsStore, usersStore,resourcesStore, selectionStore }) {
    super({ foldersStore, documentsStore, agentsStore, selectionStore });
    this.filesStore = filesStore;
    this.usersStore = usersStore;

    this.resourcesStore = resourcesStore;
    this.renameDocumentModalStore = new RenameModalStore();
    this.renameFolderModalStore = new RenameModalStore();
    
    // Register sync callbacks for automatic isSelected updates
    this.setupSelectionSync();

    makeObservable(this, {
      // View mode
      viewMode: observable,
      
      // Move modal observables
      isMoveModalOpen: observable,
      itemIdBeingMoved: observable,
      itemTypeBeingMoved: observable,

      // Share modal
      isShareModalOpen: observable,
      itemsToShare: observable,
      currentSelectionType: observable,
      selectedUsers: observable,
      selectedPermission: observable,
      pendingGrantees: observable,
      isLoadingSharing: observable,

      // Assign agent modal
      isAssignAgentModalOpen: observable,
      itemsToAssignAgent: observable,
      selectedAgents: observable,
      pendingAgents: observable,
      currentAgents: observable,
      isLoadingAgents: observable,
      isLoadingAssign: observable,

      // Duplicate modal
      isDuplicateModalOpen: observable,
      itemToDuplicate: observable,

      // Filter state
      selectedOwner: observable,
      
      loadSharedData: flow,
      openRenameDocumentModal: action,
      openRenameFolderModal: action,
      openMoveModal: action,
      closeMoveModal: action,
      setViewMode: action,
      openShareModal: action,
      closeShareModal: action,
      setSelectedUsers: action,
      setSelectedPermission: action,
      addToPendingGrantees: action,
      removePendingGrantee: action,
      updatePendingGranteePermission: action,
      shareAllPendingGrantees: flow,
      removeAccessFromItem: flow,
      updatePermissionForGrantee: flow,

      // Assign agent actions
      openAssignAgentModal: action,
      closeAssignAgentModal: action,
      setSelectedAgents: action,
      addToPendingAgents: action,
      removePendingAgent: action,
      removeAgent: action,
      assignAgentsToDocument: flow,

      // Duplicate actions
      openDuplicateModal: action,
      closeDuplicateModal: action,
      duplicateItem: flow,

      // Filter actions
      setSelectedOwner: action,

      // Computed
      shouldShowGranteesList: computed,
      duplicateModalTitle: computed,
      duplicateModalDescription: computed,
      filteredFolders: override,
      filteredDocuments: override,
    });
  }

  // Setup automatic synchronization between SelectionStore and local isSelected states
  setupSelectionSync = () => {
    // Clean up existing callbacks first
    if (this.folderSyncCallback) {
      this.selectionStore.unregisterSyncCallback('folder', this.folderSyncCallback);
    }
    if (this.documentSyncCallback) {
      this.selectionStore.unregisterSyncCallback('document', this.documentSyncCallback);
    }

    // Folder sync callback
    this.folderSyncCallback = (selectedFolders) => {
      this.folderList.forEach(folder => {
        const shouldBeSelected = selectedFolders.includes(folder.id);
        if (folder.isSelected !== shouldBeSelected) {
          folder.setSelected(shouldBeSelected);
        }
      });
    };

    // Document sync callback
    this.documentSyncCallback = (selectedDocuments) => {
      this.documentList.forEach(document => {
        const shouldBeSelected = selectedDocuments.includes(document.id);
        if (document.isSelected !== shouldBeSelected) {
          document.setSelected(shouldBeSelected);
        }
      });
    };

    // Register callbacks
    this.selectionStore.registerSyncCallback('folder', this.folderSyncCallback);
    this.selectionStore.registerSyncCallback('document', this.documentSyncCallback);
  };

  openRenameDocumentModal = (document) => {
    this.renameDocumentModalStore.open({
      type: 'document',
      defaultValue: document.name,
      resourceId: document.id,
      onConfirm: ({ name, resourceId }) => {
        const doc = this.documentList.find(d => d.id === resourceId);
        if (doc) {
          doc.setName(name);
          this.setDocumentInList(doc);
        }
        this.renameDocumentModalStore.close();
      }
    });
  };

  openRenameFolderModal = (folder) => {
    this.renameFolderModalStore.open({
      type: 'folder',
      defaultValue: folder.name,
      resourceId: folder.id,
      onConfirm: ({ name, resourceId }) => {
        const fol = this.folderList.find(f => f.id === resourceId);
        if (fol) {
          fol.setName(name);
          this.setFolderInList(fol);
        }
        this.renameFolderModalStore.close();
      }
    });
  };

  setDocumentInList(document) {
    const documentIndex = this.documentList.findIndex(doc => doc.id === document.id);
    if (documentIndex !== -1) {
      this.documentList[documentIndex] = document;
    }
  }

  setFolderInList(folder) {
    const folderIndex = this.folderList.findIndex(fol => fol.id === folder.id);
    if (folderIndex !== -1) {
      this.folderList[folderIndex] = folder;
    }
  }

  // View mode action
  setViewMode = (mode) => {
    this.viewMode = mode;
  };

  // Filter actions
  setSelectedOwner = (owner) => {
    this.selectedOwner = owner;
  };

  // Move modal methods
  openMoveModal = (itemId, itemType) => {
    this.itemIdBeingMoved = itemId;
    this.itemTypeBeingMoved = itemType;
    this.isMoveModalOpen = true;
  };

  closeMoveModal = () => {
    this.isMoveModalOpen = false;
    this.itemIdBeingMoved = null;
    this.itemTypeBeingMoved = null;
  };

  // Share modal methods
  openShareModal = (item, type) => {
    this.itemsToShare = [];
    this.currentSelectionType = null;
    
    if (this.selectionStore.selectedFolders.length > 0 || this.selectionStore.selectedDocuments.length > 0) {
      const hasSelectedFolders = this.selectionStore.selectedFolders.length > 0;
      const hasSelectedDocuments = this.selectionStore.selectedDocuments.length > 0;
      
      if (hasSelectedFolders && hasSelectedDocuments) {
        return {
          success: false,
          title: 'Invalid Selection',
          message: 'Cannot share folders and documents at the same time.',
          variant: 'warning'
        };
      }
      
      if (hasSelectedFolders) {
        const selectedFolderItems = this.folderList
          .filter(folder => this.selectionStore.selectedFolders.includes(folder.id));
        this.itemsToShare = selectedFolderItems;
        this.currentSelectionType = 'folder';
      }
      
      if (hasSelectedDocuments) {
        const selectedDocumentItems = this.documentList
          .filter(doc => this.selectionStore.selectedDocuments.includes(doc.id));
        this.itemsToShare = selectedDocumentItems;
        this.currentSelectionType = 'document';
      }
    } else if (item) {
      this.itemsToShare = [item];
      this.currentSelectionType = type;
    }

    this.isShareModalOpen = true;
    return { success: true };
  };

  closeShareModal = () => {
    this.isShareModalOpen = false;
    this.itemsToShare = [];
    this.currentSelectionType = null;
    this.selectedUsers = [];
    this.selectedPermission = '';
    this.pendingGrantees = [];
  };

  setSelectedUsers = (users) => {
    this.selectedUsers = users;
  };

  setSelectedPermission = (permission) => {
    this.selectedPermission = permission;
  };

  addToPendingGrantees = () => {
    if (!this.selectedUsers || this.selectedUsers.length === 0 || !this.selectedPermission) {
      return {
        success: false,
        title: 'Missing Information',
        message: 'Please select users and permission level.',
        variant: 'warning'
      };
    }

    const newGrantees = this.selectedUsers.map(user => ({
      grantee_uuid: user.value || user.user_uuid,
      grantee_type: 'user',
      permission: this.selectedPermission,
      grantee_full_name: user.label || user.full_name,
      grantee_email: user.email
    }));

    this.pendingGrantees = [...this.pendingGrantees, ...newGrantees];
    this.selectedUsers = [];
    this.selectedPermission = '';

    return { success: true };
  };

  removePendingGrantee = (granteeUuid) => {
    this.pendingGrantees = this.pendingGrantees.filter(
      g => g.grantee_uuid !== granteeUuid
    );
  };

  updatePendingGranteePermission = (granteeUuid, newPermission) => {
    const grantee = this.pendingGrantees.find(g => g.grantee_uuid === granteeUuid);
    if (grantee) {
      grantee.permission = newPermission;
    }
  };

  *shareAllPendingGrantees() {
    if (this.pendingGrantees.length === 0) {
      return {
        success: false,
        title: 'No Users Selected',
        message: 'Please add users to share with',
        variant: 'warning'
      };
    }

    try {
      this.isLoadingSharing = true;
      yield Promise.resolve(); // Simular async
      
      this.pendingGrantees = [];
      this.closeShareModal();

      return {
        success: true,
        title: 'Success',
        message: 'Items shared successfully',
        variant: 'success'
      };
    } catch (error) {
      return {
        success: false,
        title: 'Error',
        message: 'Failed to share items',
        variant: 'error'
      };
    } finally {
      this.isLoadingSharing = false;
    }
  }

  *removeAccessFromItem(granteeUuid, granteeType) {
    try {
      const item = this.itemsToShare[0];
      const entityType = item.type === 'folder' ? 'VAULT' : 'DOCUMENT';
      const entityUuid = item.type === 'folder' ? item.folder_uuid : item.document_uuid;

      const users = granteeType === 'user' ? [granteeUuid] : [];
      const groups = granteeType === 'group' ? [granteeUuid] : [];

      yield this.resourcesStore.removeGrant(entityType, entityUuid, users, groups);
      
      return { 
        success: true,
        title: 'Success',
        message: 'Access removed successfully',
        variant: 'success'
      };
    } catch (error) {
      return { 
        success: false,
        title: 'Error', 
        message: error.message || 'An unexpected error occurred',
        variant: 'error'
      };
    }
  }

  *updatePermissionForGrantee(granteeUuid, granteeType, newPermission) {
    try {
      const item = this.itemsToShare[0];
      const entityType = item.type === 'folder' ? 'VAULT' : 'DOCUMENT';
      const entityUuid = item.type === 'folder' ? item.folder_uuid : item.document_uuid;

      const privilegeUuids = this.resourcesStore.privileges
        .filter(p => p.privilegeName === newPermission)
        .map(p => p.entityPrivilegeUuid);

      const users = granteeType === 'user' ? [granteeUuid] : [];
      const groups = granteeType === 'group' ? [granteeUuid] : [];

      yield this.resourcesStore.updateGrant(entityType, entityUuid, privilegeUuids, users, groups);
      
      return { 
        success: true,
        title: 'Success',
        message: 'Permission updated successfully',
        variant: 'success'
      };
    } catch (error) {
      return { 
        success: false,
        title: 'Error', 
        message: error.message || 'An unexpected error occurred',
        variant: 'error'
      };
    }
  }

  get shouldShowGranteesList() {
    return this.itemsToShare.length === 1;
  }

  // Assign Agent Modal Methods
  openAssignAgentModal = (item) => {
    this.itemsToAssignAgent = [item];
    this.isAssignAgentModalOpen = true;
    
    // Load agents if not already loaded
    if (this.agentsStore.agents.length === 0) {
      this.agentsStore.fetchAgents();
    }
    
    return { success: true };
  };

  closeAssignAgentModal = () => {
    this.isAssignAgentModalOpen = false;
    this.itemsToAssignAgent = [];
    this.selectedAgents = [];
    this.pendingAgents = [];
  };

  setSelectedAgents = (agents) => {
    this.selectedAgents = agents;
  };

  addToPendingAgents = () => {
    if (this.selectedAgents.length === 0) {
      return {
        success: false,
        title: 'No Agents Selected',
        message: 'Please select at least one agent.',
        variant: 'warning'
      };
    }

    this.pendingAgents = [...this.pendingAgents, ...this.selectedAgents];
    this.selectedAgents = [];
    return { success: true };
  };

  removePendingAgent = (agentId) => {
    this.pendingAgents = this.pendingAgents.filter(agent => agent.id !== agentId);
  };

  removeAgent = (agentId) => {
    this.currentAgents = this.currentAgents.filter(agent => agent.id !== agentId);
  };

  *assignAgentsToDocument() {
    try {
      this.isLoadingAssign = true;
      
      // Simulate API call
      yield new Promise(resolve => setTimeout(resolve, 1000));
      
      this.closeAssignAgentModal();
      
      return {
        success: true,
        title: 'Success',
        message: 'Agents added to the list',
        variant: 'success'
      };
    } catch (error) {
      return {
        success: false,
        title: 'Error',
        message: 'Failed to assign agents',
        variant: 'error'
      };
    } finally {
      this.isLoadingAssign = false;
    }
  }

  // Duplicate Modal Methods
  openDuplicateModal = (item) => {
    this.itemToDuplicate = item;
    this.isDuplicateModalOpen = true;
    return { success: true };
  };

  closeDuplicateModal = () => {
    this.isDuplicateModalOpen = false;
    this.itemToDuplicate = null;
  };

  *duplicateItem() {
    try {
      if (!this.itemToDuplicate) {
        return {
          success: false,
          title: 'No Item Selected',
          message: 'Please select an item to duplicate.',
          variant: 'warning'
        };
      }

      // Simulate API call
      yield new Promise(resolve => setTimeout(resolve, 1000));
      
      this.closeDuplicateModal();
      
      return {
        success: true,
        title: 'Success',
        message: 'Item duplicated successfully',
        variant: 'success'
      };
    } catch (error) {
      return {
        success: false,
        title: 'Error',
        message: 'Failed to duplicate item',
        variant: 'error'
      };
    }
  }

  get duplicateModalTitle() {
    if (!this.itemToDuplicate) return 'Duplicate Item';
    return `Duplicate "${this.itemToDuplicate.name || this.itemToDuplicate.title}"`;
  }

  get duplicateModalDescription() {
    if (!this.itemToDuplicate) return 'Are you sure you want to duplicate this item?';
    return `Create a copy of "${this.itemToDuplicate.name || this.itemToDuplicate.title}"?`;
  }

  // Override filteredFolders to include owner filter
  get filteredFolders() {
    let filtered = this.folderList;

    // Text filter from base class
    if (this.filterText) {
      filtered = filtered.filter(folder => {
        const title = folder.title || folder.name || '';
        return title.toLowerCase().includes(this.filterText.toLowerCase());
      });
    }

    // Owner filter
    if (this.selectedOwner) {
      const ownerValue = Array.isArray(this.selectedOwner) 
        ? this.selectedOwner[0] 
        : this.selectedOwner;
      
      if (ownerValue) {
        const ownerId = typeof ownerValue === 'object' && ownerValue !== null
          ? ownerValue.value
          : ownerValue;
        
        filtered = filtered.filter(folder => {
          // Match by document ID (since ownerOptions uses document.id as value)
          // or by owner_full_name
          return folder.owner_uuid === ownerId || 
                 (folder.owner_full_name && folder.owner_full_name.toLowerCase().includes(String(ownerId).toLowerCase()));
        });
      }
    }

    return this.filterSortStore.sortItems(filtered);
  }

  // Override filteredDocuments to include owner filter
  get filteredDocuments() {
    let filtered = this.documentList;

    // Text filter from base class
    if (this.filterText) {
      filtered = filtered.filter(document => {
        const title = document.title || document.name || '';
        return title.toLowerCase().includes(this.filterText.toLowerCase());
      });
    }

    // Owner filter
    if (this.selectedOwner) {
      const ownerValue = Array.isArray(this.selectedOwner) 
        ? this.selectedOwner[0] 
        : this.selectedOwner;
      
      if (ownerValue) {
        const ownerId = typeof ownerValue === 'object' && ownerValue !== null
          ? ownerValue.value
          : ownerValue;
        
        filtered = filtered.filter(document => {
          // Match by document ID (since ownerOptions uses document.id as value)
          return document.id === ownerId;
        });
      }
    }

    return this.filterSortStore.sortItems(filtered);
  }

  // Load Data Actions
  *loadSharedData() {
    try {
      this.isLoading = true;
      this.error = null;
      const [folders, documents] = yield Promise.all([
        this.foldersStore.getSharedFolders(),
        this.documentsStore.getSharedDocuments()
      ]);
      
      this.setFolderList(folders);
      this.setDocumentList(documents);
      
      // Setup sync after data is loaded
      this.setupSelectionSync();
    } catch (error) {
      this.error = error;
      console.error('Error loading shared data:', error);
    } finally {
      this.isLoading = false;
    }
  }
}

export default SharedWithMeStore;